import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { MenuItem, Select } from '@mui/material';

import { ParentPaper } from '../atoms/parent-paper';
import { SwitchBox } from '../molecules/switch-box';
import { ChartBox } from '../molecules/chart-box';
import { Group } from '../organisms/group';
import { FormattedValue } from '../store/formatted-value';
import { SliderBox } from '../molecules/slider-box';
import { ValueBox } from '../molecules/value-box';
import { Thermometer } from '../atoms/icons/thermometer';
import { useSelector } from '../store/utils';
import { ControlBox } from '../atoms/control-box';
import { FormattedStatus } from '../store/formatted-status';

const Thermostat = () => {
  const [thermostatIndex, setThermostatIndex] = useState(0);

  const [thermostatCount, thermostatNames] = useSelector(
    ({ variables }) =>
      [
        Math.min(
          variables.THERMOSTAT_SETTINGS.length,
          variables.NUMBER_OF_THERMOSTATS
        ),
        variables.THERMOSTAT_SETTINGS,
      ] as const
  );

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="thermostat.status"
              defaultMessage="Režim termostatu"
            />
          }
          subtitle={<FormattedStatus type="thermostat" />}
          fullWidth
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="thermostat.general"
            defaultMessage="Společné nastavení"
          />
        }
        visibility={{
          variableIds: [{ variableId: 'HP_ENABLED', reversed: true }],
        }}
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.general.heating"
                defaultMessage="Vytápění aktivní"
              />
            }
            switchVariableId={'HP_ROOM_ON'}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.general.cooling"
                defaultMessage="Chlazení aktivní"
              />
            }
            switchVariableId={'HP_ROOM_COOL_ON'}
            fullWidth
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="thermostat.general.cooling.pvstability"
                defaultMessage="Potenciál aktivace zdroje"
              />
            }
            value={
              <FormattedValue
                variableId="THERMOSTAT_PV_FLEXIBILITY_PERC"
                opts={{
                  style: 'percent',
                  maximumFractionDigits: 0,
                }}
              />
            }
          />
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage id="thermostat.range" defaultMessage="Termostat" />
        }
        layout="list"
      >
        {thermostatCount > 1 && (
          <ControlBox
            title="Termostat"
            fullWidth
            control={
              <Select
                value={thermostatIndex}
                onChange={(e) => setThermostatIndex(e.target.value as number)}
              >
                {Array(thermostatCount)
                  .fill(null)
                  .map((_, i) => {
                    return (
                      <MenuItem value={i} key={i}>
                        {thermostatNames[i].name}
                      </MenuItem>
                    );
                  })}
              </Select>
            }
          />
        )}
        <ParentPaper>
          <ChartBox
            variableId="THERMOSTAT_TEMP_GRAPH"
            variableSuffix={
              thermostatIndex === 0 ? undefined : String(thermostatIndex)
            }
          />
        </ParentPaper>
        <ParentPaper>
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="thermostat.range.temperature"
                defaultMessage="Aktuální teplota"
              />
            }
            value={
              <FormattedValue
                variableId={`THERMOSTAT_INFO[${thermostatIndex}].actualTemp`}
                opts={{
                  style: 'unit',
                  unit: 'celsius',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            }
            icon={<Thermometer />}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.on"
                defaultMessage="Termostat aktivní"
              />
            }
            switchVariableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].on`}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.status"
                defaultMessage="Režim termostatu"
              />
            }
            subtitle={
              <FormattedStatus type="thermostat" index={thermostatIndex} />
            }
            visibility={{
              variableId: `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
            }}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.flexibility_status"
                defaultMessage="Režim flexibility termostatu"
              />
            }
            subtitle={
              <FormattedStatus
                type="thermostatFlexibility"
                index={thermostatIndex}
              />
            }
            visibility={{
              variableId: `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
            }}
            fullWidth
          />
        </ParentPaper>
        <ParentPaper>
          <SliderBox
            title={
              <FormattedMessage
                id="thermostat.range.set_temperature"
                defaultMessage="Nastavená teplota"
              />
            }
            subtitle={
              <FormattedMessage
                id="thermostat.range.on.subtitle"
                defaultMessage="Základní teplota"
              />
            }
            variableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].setTemp`}
            visibility={{
              variableId: `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
            }}
            max={80}
            min={10}
            step={0.5}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1,
            }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="thermostat.range.flexibility-temperature"
                defaultMessage="Flexibilní teplota"
              />
            }
            subtitle={
              <FormattedMessage
                id="thermostat.range.flexibility-temperature.subtitle"
                defaultMessage="Teplota o kterou je možné předehřát nebo předchladit dům"
              />
            }
            variableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].flexibilityTemp`}
            visibility={{
              variableId: `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
            }}
            max={50}
            min={0}
            step={0.1}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1,
            }}
          />
        </ParentPaper>
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.pv-flexibility"
                defaultMessage="Řízení podle fotovoltaiky"
              />
            }
            switchVariableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].pvFlexibilityActiveSet`}
            visibility={{
              variableId: `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
            }}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.sp-flexibility-up"
                defaultMessage="Zvýšení spotřeby podle spotu"
              />
            }
            switchVariableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].spFlexibilityActiveUpSet`}
            visibility={{
              variableIds: [
                `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
                'SP_ON',
                'SP_INSTALL',
              ],
            }}
            fullWidth
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="thermostat.range.sp-flexibility-down"
                defaultMessage="Odložení spotřeby podle spotu"
              />
            }
            switchVariableId={`THERMOSTAT_SETTINGS[${thermostatIndex}].spFlexibilityActiveDownSet`}
            visibility={{
              variableIds: [
                `THERMOSTAT_SETTINGS[${thermostatIndex}].on`,
                'SP_ON',
                'SP_INSTALL',
              ],
            }}
            fullWidth
          />
        </ParentPaper>
      </Group>
    </>
  );
};

export default Thermostat;
